import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom'; // Import Link
import { Box, Typography, CircularProgress } from '@mui/material';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
// Import Moon and Sun icons for theme toggle
import { LogOut, User, CreditCard, Settings, LifeBuoy, Share2, Menu as MenuIcon, Mail, Archive, Bell, Moon, Sun, BarChart3, MapPin, Download, BookOpen, Smartphone } from 'lucide-react'; // Added Download, BookOpen, Smartphone
import { FaWhatsapp } from 'react-icons/fa';
import MainNav from '../ui/MainNav';
import coolyLogo from '../../assets/coolyLogo.png';
import { ROUTES } from '../../App';
import { useToast } from '../../hooks/use-toast';
import { ADMIN_EMAILS } from '../../constants';
import { useTheme } from '../ui/theme-provider'; // Import useTheme hook
import { usePWAInstall } from '../../hooks/usePWAInstall'; // Import PWA install hook
import PWAInstallGuide from '../ui/PWAInstallGuide'; // Import PWA install guide
import Sidebar from './Sidebar';

// Accept currentUser prop
const MainLayout = ({ user, currentUser, isAdmin, handleSignOut, subscriptionStatus }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [initials, setInitials] = useState('');
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const { toast } = useToast(); // Initialize toast
  const { theme, toggleTheme } = useTheme(); // Add theme context
  const [redirectAttempted, setRedirectAttempted] = useState(false);
  // PWA install functionality
  const { showInstallButton, platform, installApp, dismissInstallPrompt } = usePWAInstall();
  const [showPWAGuide, setShowPWAGuide] = useState(false);

  // Check if current path is the billing path - this prevents redirect loops
  const isBillingPath = location.pathname === ROUTES.BILLING;

  // Check if onboarding is complete
  // Corrected to check subscriptionStatus.subscription.status
  const isOnboardingComplete =
    currentUser?.consentGiven === 'true' &&
    currentUser?.profileComplete === true &&
    (currentUser?.billingComplete === true ||
     currentUser?.onboardingComplete === true ||
     (subscriptionStatus?.subscription?.status === 'active' || subscriptionStatus?.subscription?.status === 'trialing') // Corrected check
    );

  // Effect to redirect users if onboarding is not complete
  useEffect(() => {
    // Avoid redirect loops
    if (redirectAttempted) {
      console.log("MainLayout: Redirect already attempted, avoiding loop");
      return;
    }

    // If user is an admin, bypass all onboarding redirection logic in this layout
    if (isAdmin) {
      console.log("MainLayout: Admin user, bypassing onboarding redirect logic.");
      return;
    }

    if (user && !isOnboardingComplete) {
      // Determine which onboarding step the user needs to complete
      if (currentUser?.consentGiven !== 'true') {
        console.log('MainLayout: Privacy consent needed, redirecting to home');
        navigate(ROUTES.HOME, { replace: true });
        setRedirectAttempted(true);
        return;
      } 
      
      if (currentUser?.profileComplete !== true) {
        console.log('MainLayout: Profile completion needed, redirecting to profile');
        navigate(ROUTES.PROFILE, { replace: true, state: { onboarding: true } });
        setRedirectAttempted(true);
        return;
      }
      
      if (!isBillingPath && currentUser?.billingComplete !== true && subscriptionStatus?.status !== 'active') {
        console.log('MainLayout: Billing completion needed, redirecting to billing');
        navigate(ROUTES.BILLING, { 
          replace: true,
          state: { onboarding: true, forceShowPlans: true } 
        });
        setRedirectAttempted(true);
        return;
      }
    }
  }, [
    user, 
    currentUser, 
    isOnboardingComplete, 
    subscriptionStatus, // subscriptionStatus itself is a dependency
    navigate, 
    redirectAttempted, 
    isBillingPath,
    isAdmin // Add isAdmin to the dependency array
    // Add isOnboardingComplete to dependency array if it's derived from props/state
    // and its change should trigger the effect.
  ]);

  // Reset redirect flag when location changes
  useEffect(() => {
    setRedirectAttempted(false);
  }, [location.pathname]);

  useEffect(() => {
    // Prioritize currentUser.name for initials if available
    const nameToUse = currentUser?.name || user?.displayName;
    if (nameToUse) {
      setInitials(nameToUse.split(' ').map(n => n[0]).join(''));
    } else if (user?.email) {
      setInitials(user.email[0].toUpperCase());
    }
  }, [user, currentUser]); // Add currentUser dependency

  useEffect(() => {
    setIsMobileNavOpen(false);
  }, [navigate]); // Assuming navigate changes trigger this, might need location instead if not

  // Show loading when onboarding status is being checked
  const showLoadingIndicator =
    user &&
    !isOnboardingComplete &&
    redirectAttempted &&
    !isBillingPath &&
    location.pathname !== ROUTES.PROFILE &&
    location.pathname !== ROUTES.HOME;

  // Now do the conditional loading check
  if (showLoadingIndicator) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column', 
        gap: 2 
      }}>
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">
          Completing your account setup...
        </Typography>
      </Box>
    );
  }

  // Generic share data
  const shareContent = {
    title: 'Transform Your Dental Practice with Coooly!',
    text: 'Discover Coooly - the game-changing invoicing platform designed specifically for dental professionals - be it hygienists, therapists, locum dentists or nurses!\n\nWhat makes Coooly special:\n• Streamlined invoicing tailored for dental professionals\n• Smart practice management tools\n• Professional invoice templates\n• Secure payment tracking\n• Time-saving automation\n\nSpecial Offer: Get 2 months completely FREE - no credit card required!\n\nJoin hundreds of dental professionals who\'ve already simplified their business with Coooly.',
    url: window.location.origin
  };

  // Generic share functionality (Web Share API or Clipboard) - Keep as fallback/other
  const handleGenericShare = async () => {
    try {
      if (navigator.share) {
        console.log('Attempting generic share using navigator.share with data:', shareContent);
        await navigator.share(shareContent);
        console.log('Content shared successfully via navigator.share');
      } else {
        // Fallback: Copy text and URL to clipboard
        console.log('navigator.share not supported, using copy-to-clipboard fallback.');
        const textToCopy = `${shareContent.text}\n\nCheck it out here: ${shareContent.url}`;
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(textToCopy);
          toast({
            title: "Copied to Clipboard",
            description: "Shareable link and message copied!",
            variant: "success",
          });
        } else {
          alert('Sharing not supported. Please manually copy this link:\n' + shareContent.url + '\n\n' + shareContent.text);
        }
      }
    } catch (err) {
      console.error('Error during generic share:', err.name, err.message);
      if (err.name !== 'AbortError') {
        toast({
          title: "Share Error",
          description: `Could not share content: ${err.message}`,
          variant: "destructive",
        });
      } else {
        console.log('Generic share action cancelled by user.');
      }
    }
  };

  // Specific share via email functionality
  const handleShareViaEmail = () => {
    const subject = encodeURIComponent('Streamline Your Dental Invoicing with Coooly - 2 Months Free!');
    const emailBody = `Hi there!

I wanted to share something amazing I discovered - Coooly, a revolutionary invoicing platform built specifically for dental professionals - be it hygienists, therapists, locum dentists or nurses!

Why Coooly is perfect for dental professionals:
• Purpose-built invoicing for our industry
• Professional templates that impress practices
• Smart practice management features
• Secure payment tracking
• Automated workflows that save hours

Best part? You can try it FREE for 2 months - no credit card needed!

I've been using it and it's completely transformed how I handle my invoicing and practice management. The time I save each week is incredible!

Check it out here: ${shareContent.url}

Trust me, you'll wonder how you managed without it!

Best regards,
${currentUser?.name || user?.displayName || 'A fellow dental professional'}`;

    const body = encodeURIComponent(emailBody);
    const mailtoLink = `mailto:?subject=${subject}&body=${body}`;
    try {
      window.location.href = mailtoLink;
      console.log('Attempting to open mail client with:', mailtoLink);
    } catch (error) {
      console.error('Failed to open mail client:', error);
      toast({
        title: "Error",
        description: "Could not open email client automatically.",
        variant: "destructive",
      });
      // As a fallback, copy the body content?
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(`${shareContent.text}\n\n${shareContent.url}`);
        toast({
          title: "Copied to Clipboard",
          description: "Email content copied. Please paste it into your email client.",
          variant: "info", // Use info variant for this fallback
        });
      }
    }
  };

  // Specific share via WhatsApp functionality
  const handleShareViaWhatsApp = () => {
    const whatsappMessage = `Hey! I found this fantastic tool for dental professionals!

*Coooly* is a game-changer for dental professionals - be it hygienists, therapists, locum dentists or nurses!

What I love about it:
• Built specifically for dental professionals
• Professional invoicing made simple
• Smart practice management tools
• Secure payment tracking
• Saves me hours every week

*FREE for 2 months* - no credit card required!

I've been using it and honestly can't imagine going back to the old way of doing things.

Check it out: ${shareContent.url}

You'll thank me later!`;

    const text = encodeURIComponent(whatsappMessage);
    const whatsappLink = `https://wa.me/?text=${text}`;
    try {
      // Open in a new tab for web.whatsapp.com or to trigger the app
      window.open(whatsappLink, '_blank', 'noopener,noreferrer');
      console.log('Attempting to open WhatsApp with:', whatsappLink);
    } catch (error) {
      console.error('Failed to open WhatsApp:', error);
      toast({
        title: "Error",
        description: "Could not open WhatsApp automatically.",
        variant: "destructive",
      });
      // Fallback to copy?
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(`${shareContent.text}\n\n${shareContent.url}`);
        toast({
          title: "Copied to Clipboard",
          description: "WhatsApp message copied. Please paste it into WhatsApp.",
          variant: "info",
        });
      }
    }
  };

  // Toggle mobile navigation
  const toggleMobileNav = () => {
    setIsMobileNavOpen(!isMobileNavOpen);
  };

  // Sidebar component extracted for clarity
  const SidebarContent = () => (
    <>
      <div className="mb-6">
        {/* MODIFIED: Link to role-specific dashboard */}
        <Link to={currentUser?.role === 'Practice Manager' ? ROUTES.PRACTICE_DASHBOARD : ROUTES.DASHBOARD} onClick={() => setIsMobileNavOpen(false)}>
          <img src={coolyLogo} alt="Coooly Logo" className="w-32 mx-auto" />
        </Link>
      </div>
      <MainNav 
        user={user} 
        currentUser={currentUser} 
        subscription={subscriptionStatus} 
        onLinkClick={() => setIsMobileNavOpen(false)} 
        isAdmin={isAdmin}
        userRole={currentUser?.role} // ADDED: Pass userRole
      />
      <div className="mt-auto">
        {/* Optional: Add footer links here if needed */}
      </div>
    </>
  );
  const toggleSidebar = () => {
    setSidebarOpen(prev => !prev);
  };

  // PWA install handler
  const handlePWAInstall = async () => {
    // For platforms with native install prompt
    if (platform === 'android-chrome' || platform === 'desktop-chrome' || platform === 'desktop-edge') {
      const result = await installApp();
      
      if (result === 'fallback') {
        setShowPWAGuide(true);
      } else if (!result) {
        setShowPWAGuide(true);
      }
    } else {
      // For iOS and other platforms, show the guide
      setShowPWAGuide(true);
    }
    setIsMobileNavOpen(false);
  };

  // Get PWA install content based on platform
  const getPWAContent = () => {
    switch (platform) {
      case 'ios-safari':
        return {
          icon: <Smartphone className="mr-2 h-4 w-4" />,
          text: 'Add to Home Screen'
        };
      case 'android-chrome':
        return {
          icon: <Download className="mr-2 h-4 w-4" />,
          text: 'Install App'
        };
      default:
        return {
          icon: <Download className="mr-2 h-4 w-4" />,
          text: 'Install App'
        };
    }
  };  return (
    <div className="flex h-screen bg-muted/40">
      <Sidebar
        user={user}
        currentUser={currentUser}
        isAdmin={isAdmin}
        subscriptionStatus={subscriptionStatus}
        userRole={currentUser?.role}
        open={sidebarOpen}
        setOpen={setSidebarOpen}
      />
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header component - assuming it's a separate component */}
        <header className="app-card-bg p-4 flex justify-end items-center border-b app-card-border space-x-4">
          {/* Hamburger Menu Button (Mobile Only) - Use app-text-primary and hover:bg-accent */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden app-text-primary hover:bg-accent mr-auto" 
            onClick={toggleSidebar}
          >
            <MenuIcon className="h-6 w-6" />
            <span className="sr-only">Toggle navigation menu</span>
          </Button>          {/* Theme Toggle Switch - Visible on all screens */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-end space-x-2 px-3 py-1 rounded-md hover:bg-accent transition-colors">
                  <Sun className="h-3.5 w-3.5 app-text-secondary mb-0.5" />
                  
                  {/* Custom toggle implementation instead of using the Switch component */}
                  <div 
                    onClick={() => {
                      console.log("Header: Toggling theme from", theme);
                      toggleTheme();
                      
                      setTimeout(() => {
                        toast({
                          title: `Switched to ${theme === "light" ? "dark" : "light"} mode`,
                          description: "The theme has been updated",
                          variant: "default",
                        });
                      }, 100);
                    }}
                    className="relative inline-flex h-3 w-8 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring mb-0.5"
                    style={{ 
                      backgroundColor: theme === "dark" ? "#7c3aed" : "#d1d5db",
                    }}
                  >
                    {/* The white dot/circle - adjusted positioning to fix right alignment in dark mode */}
                    <span
                      className="pointer-events-none inline-block rounded-full bg-white"
                      style={{
                        position: "absolute",
                        height: "10px",
                        width: "10px",
                        top: "50%",
                        // Fix dark mode position to be at the right edge, light mode at left edge
                        left: theme === "dark" ? "calc(100% - 12px)" : "2px", // Dynamic positioning based on parent width
                        transform: "translateY(-50%)",
                        boxShadow: "0 0 0 1px #7c3aed",
                        transition: "left 150ms cubic-bezier(0.16, 1, 0.3, 1)"
                      }}
                    ></span>
                  </div>
                  
                  <Moon className="h-3.5 w-3.5 app-text-secondary mb-0.5" />
                  <span className="sr-only">Toggle between light and dark mode</span>
                </div>
              </TooltipTrigger>
              <TooltipContent 
                side="bottom" 
                className="bg-popover text-popover-foreground border border-border"
              >
                <p>Switch to {theme === "light" ? "dark" : "light"} mode</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* User Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                <Avatar className="h-8 w-8">
                  {/* Use photoURL from auth user object */}
                  <AvatarImage src={user?.photoURL} alt={currentUser?.name || user?.displayName || user?.email} />
                  {/* Apply fallback colors via inline style */}
                  <AvatarFallback 
                    style={{ 
                      backgroundColor: 'var(--app-avatar-fallback-bg) !important', 
                      color: 'var(--app-avatar-fallback-text) !important' 
                    }}
                  >
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            {/* Use app-card-bg, app-card-border, app-text-primary for dropdown */}
            {/* ADDED: z-[1050] to ensure it's above Leaflet map components (Leaflet popups are around z-index 700, map panes up to 600, markercluster z-index can also be high) */}
            <DropdownMenuContent className="w-56 app-card-bg app-card-border app-text-primary z-[1050]" align="end" forceMount>
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  {/* Use app-text-primary and app-text-secondary */}
                  <p className="text-sm font-medium leading-none app-text-primary">{currentUser?.name || user?.displayName || 'User'}</p>
                  <p className="text-xs leading-none app-text-secondary">{user?.email}</p>
                </div>
              </DropdownMenuLabel>
              {/* Use app-card-border for separator */}
              <DropdownMenuSeparator className="app-card-border" />
              {/* Use hover:bg-accent for menu items AND ensure text color */}
              <DropdownMenuItem onClick={() => { navigate(ROUTES.PROFILE); setIsMobileNavOpen(false); }} className="hover:bg-accent cursor-pointer app-text-primary">
                <User className="mr-2 h-4 w-4" />
                <span>Profile</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { navigate(ROUTES.BILLING); setIsMobileNavOpen(false); }} className="hover:bg-accent cursor-pointer app-text-primary">
                <CreditCard className="mr-2 h-4 w-4" />
                <span>Billing</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { navigate(ROUTES.SUPPORT); setIsMobileNavOpen(false); }} className="hover:bg-accent cursor-pointer app-text-primary">
                <LifeBuoy className="mr-2 h-4 w-4" />
                <span>Support</span>
              </DropdownMenuItem>
              
              {/* Theme Toggle Item */}
              <DropdownMenuItem 
                onClick={() => { 
                  console.log("Toggling theme from", theme);
                  toggleTheme(); 
                  setIsMobileNavOpen(false);
                  
                  // Verify the theme class on the root element
                  setTimeout(() => {
                    const root = document.documentElement;
                    const hasLightClass = root.classList.contains('light');
                    const hasDarkClass = root.classList.contains('dark');
                    console.log("HTML classes:", root.className);
                    console.log("Root element has light class:", hasLightClass);
                    console.log("Root element has dark class:", hasDarkClass);
                    console.log("Current theme variables:", {
                      bgPrimary: getComputedStyle(document.body).getPropertyValue('background-color'), // Check body background
                      textPrimary: getComputedStyle(document.body).getPropertyValue('color') // Check body color
                    });
                    
                    toast({
                      title: `Switched to ${theme === "light" ? "dark" : "light"} mode`,
                      description: "The theme has been updated",
                      variant: "default",
                    });
                  }, 100);
                }} 
                className="hover:bg-accent cursor-pointer app-text-primary" // Use hover:bg-accent and ensure text color
              >
                {theme === "light" ? (
                  <Moon className="mr-2 h-4 w-4" />
                ) : (
                  <Sun className="mr-2 h-4 w-4" />
                )}
                <span>{theme === "light" ? "Dark Mode" : "Light Mode"}</span>
              </DropdownMenuItem>

              {/* Share via Email Item */}
              <DropdownMenuItem onClick={handleShareViaEmail} className="hover:bg-accent cursor-pointer app-text-primary">
                <Mail className="mr-2 h-4 w-4" />
                <span>Share via Email</span>
              </DropdownMenuItem>

              {/* Share via WhatsApp Item */}
              <DropdownMenuItem onClick={handleShareViaWhatsApp} className="hover:bg-accent cursor-pointer app-text-primary">
                <FaWhatsapp className="mr-2 h-4 w-4 text-green-500" /> {/* Added WhatsApp icon */}
                <span>Share via WhatsApp</span>
              </DropdownMenuItem>              {/* Generic Share Item (for iMessage/Other) */}
              <DropdownMenuItem onClick={handleGenericShare} className="hover:bg-accent cursor-pointer app-text-primary">
                <Share2 className="mr-2 h-4 w-4" />
                <span>Share...</span> {/* Changed label */}
              </DropdownMenuItem>              {/* PWA Install Option - Show only if installable */}
              {showInstallButton && (
                <>
                  <DropdownMenuSeparator className="app-card-border" />
                  <DropdownMenuItem onClick={handlePWAInstall} className="hover:bg-accent cursor-pointer app-text-primary">
                    {getPWAContent().icon}
                    <span>{getPWAContent().text}</span>
                  </DropdownMenuItem>
                </>
              )}

              {isAdmin && (
                <>
                  {/* Use app-card-border for separator */}
                  <DropdownMenuSeparator className="app-card-border" />
                  {/* Use app-accent-purple for label */}
                  <DropdownMenuLabel className="text-xs app-accent-purple">Admin</DropdownMenuLabel>
                  {/* Add Dashboard as first admin item */}
                  <DropdownMenuItem onClick={() => { navigate(ROUTES.ADMIN_DASHBOARD); setIsMobileNavOpen(false); }} className="hover:bg-accent cursor-pointer app-text-primary">
                    <BarChart3 className="mr-2 h-4 w-4" />
                    <span>Dashboard</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { navigate(ROUTES.ADMIN_PRACTICE_MGMT); setIsMobileNavOpen(false); }} className="hover:bg-accent cursor-pointer app-text-primary">
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Practice Admin</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { navigate(ROUTES.ADMIN_NOTIFICATIONS); setIsMobileNavOpen(false); }} className="hover:bg-accent cursor-pointer app-text-primary">
                    <Bell className="mr-2 h-4 w-4" />
                    <span>Notifications</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { navigate(ROUTES.DATA_MERGE); setIsMobileNavOpen(false); }} className="hover:bg-accent cursor-pointer app-text-primary">
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Data Merge Tool</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { navigate(ROUTES.ADMIN_ARCHIVE_MANAGER); setIsMobileNavOpen(false); }} className="hover:bg-accent cursor-pointer app-text-primary">
                    <Archive className="mr-2 h-4 w-4" />
                    <span>Daily Log Archive</span>
                  </DropdownMenuItem>
                  {/* Add Email Admin Link */}
                  <DropdownMenuItem onClick={() => { navigate(ROUTES.ADMIN_EMAIL); setIsMobileNavOpen(false); }} className="hover:bg-accent cursor-pointer app-text-primary">
                    <Mail className="mr-2 h-4 w-4" /> {/* Using Mail icon for Email Admin */}
                    <span>Email Admin</span>
                  </DropdownMenuItem>
                  {/* Add Email Template Admin Link */}
                  <DropdownMenuItem onClick={() => { navigate(ROUTES.ADMIN_EMAIL_TEMPLATES); setIsMobileNavOpen(false); }} className="hover:bg-accent cursor-pointer app-text-primary">
                    <Mail className="mr-2 h-4 w-4" />
                    <span>Email Templates</span>
                  </DropdownMenuItem>
                  {/* ADDED: User Map Link */}
                  <DropdownMenuItem onClick={() => { navigate(ROUTES.ADMIN_USER_MAP); setIsMobileNavOpen(false); }} className="hover:bg-accent cursor-pointer app-text-primary">
                    <MapPin className="mr-2 h-4 w-4" />
                    <span>User Map</span>
                  </DropdownMenuItem>
                </>
              )}
              {/* Use app-card-border for separator */}
              <DropdownMenuSeparator className="app-card-border" />
              <DropdownMenuItem onClick={handleSignOut} className="hover:bg-accent cursor-pointer app-text-primary">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>        {/* Page Content - Use app-bg-primary for the main content area */}
        {/* The className="flex-1" will make this main element grow to fill available space in its flex column parent */}
        <main className="flex-1 p-4 md:p-6 overflow-auto app-bg-primary w-full lg:pl-64">
          <Outlet context={{ user, currentUser, subscriptionStatus }} />
        </main>
      </div>

      {/* PWA Install Guide Modal */}
      <PWAInstallGuide
        isOpen={showPWAGuide}
        onClose={() => setShowPWAGuide(false)}
        platform={platform}
      />
    </div>
  );
};

export default MainLayout;
